import { REPORTS_TYPES } from '../constants';
import SALES_UI_SCHEMA from '../salesUISchema';
import { NO_GROUP_FILTER_OPTION_ID, NO_TAG_FILTER_OPTION_ID } from '../../shifts/constants';

class ReportsUtils {
  constructor(Authentication, LaborUISchema, Utils, PersonaCollection, roundFilter, sdk) {
    'ngInject';

    Object.assign(this, { Authentication, LaborUISchema, Utils, PersonaCollection, roundFilter });
    this.getSchemaFields = sdk('process/getSchemaFields');
    this.tallyAggregates = sdk('process/tallyAggregates');
  }

  groupByDates(data, from, to) {
    return REPORTS_TYPES.reduce(
      (grouped, type) => ({
        ...grouped,
        [type]: data[type].reduce((reduced, item) => {
          if (item.date in reduced) {
            reduced[item.date].push(item);
          }

          return reduced;
        }, this.getEmptyDictsForRange(from, to)),
      }),
      {}
    );
  }

  getEmptyDictsForRange(from, to) {
    return this.Utils.getDatesBetween(from, to)
      .map(this.Utils.formatDate)
      .reduce((obj, date) => ((obj[date] = []), obj), {});
  }

  isShownByLocationFilter(filter, reportEntity) {
    return (
      !filter.locationIds?.length ||
      !reportEntity.location ||
      filter.locationIds.includes(reportEntity.location.id)
    );
  }

  isShownByUserFilter(filter, reportEntity) {
    return (
      !filter.userIds?.length || !reportEntity.user || filter.userIds.includes(reportEntity.user.id)
    );
  }

  isShownByPositionFilter(filter, reportEntity) {
    return (
      !filter.positionIds?.length ||
      !reportEntity.position ||
      filter.positionIds.includes(reportEntity.position.id)
    );
  }

  isShownByGroupFilter(filter, reportEntity) {
    return (
      !filter.groupIds?.length ||
      !reportEntity.user ||
      (filter.groupIds.includes(NO_GROUP_FILTER_OPTION_ID) && !reportEntity.user.groups.length) ||
      filter.groupIds.some((groupId) =>
        reportEntity.user.groups.some((group) => group.id === groupId)
      )
    );
  }

  isShownByTagsFilter(filter, reportEntity) {
    return (
      !filter.tagIds?.length ||
      (filter.tagIds.includes(NO_TAG_FILTER_OPTION_ID) && !reportEntity.tags?.length) ||
      reportEntity.tags?.some(({ id: tagId }) => filter.tagIds.includes(tagId))
    );
  }

  isShownByPayTypeFilter(filter, reportEntity) {
    return (
      !filter.payTypes?.length ||
      !reportEntity.payType ||
      filter.payTypes.includes(reportEntity.payType)
    );
  }

  isEntityShownByFilters(filter, reportEntity) {
    return (
      this.isShownByUserFilter(filter, reportEntity) &&
      this.isShownByLocationFilter(filter, reportEntity) &&
      this.isShownByPositionFilter(filter, reportEntity) &&
      this.isShownByGroupFilter(filter, reportEntity) &&
      this.isShownByTagsFilter(filter, reportEntity) &&
      this.isShownByPayTypeFilter(filter, reportEntity)
    );
  }

  filterReportsData(data, filter = {}) {
    return REPORTS_TYPES.reduce(
      (filtered, type) => ({
        ...filtered,
        [type]: Object.keys(data[type]).reduce(
          (reduced, day) => ({
            ...reduced,
            [day]: data[type][day].filter((reportEntity) =>
              this.isEntityShownByFilters(filter, reportEntity)
            ),
          }),
          {}
        ),
      }),
      {}
    );
  }

  getLabor(estimatedWages = 0, revenue, selectedLocationIdsArg = []) {
    const totalSalesInRelevantLocations = this.getRelevantSales(
      revenue,
      selectedLocationIdsArg
    ).total;
    return totalSalesInRelevantLocations.projectedSales > 0
      ? this.roundFilter((estimatedWages * 100) / totalSalesInRelevantLocations.projectedSales, 2)
      : NaN;
  }

  getRelevantSales(revenue, selectedLocationIdsArg = []) {
    let selectedLocationIds = [...selectedLocationIdsArg];
    if (!selectedLocationIds.length) {
      selectedLocationIds = this.PersonaCollection.locations.map((location) => location.id);
    }

    const locationSalesDict = Object.keys(revenue).reduce((reduced, day) => {
      const dayDict = revenue[day].reduce(
        (dict, item) => ({
          ...dict,
          [item.location.id]: {
            projectedSales: item.projectedSales || 0,
            actualSales: item.actualSales || 0,
          },
        }),
        {}
      );

      Object.keys(dayDict).forEach((locationId) => {
        if (!reduced[locationId]) {
          reduced[locationId] = {
            projectedSales: 0,
            actualSales: 0,
          };
        }

        reduced[locationId].projectedSales += dayDict[locationId]?.projectedSales || 0;
        reduced[locationId].actualSales += dayDict[locationId]?.actualSales || 0;
      });

      return reduced;
    }, {});

    const byLocation = selectedLocationIds.reduce((acc, locId) => {
      acc[locId] = locationSalesDict[locId];
      return acc;
    }, {});

    return {
      total: {
        projectedSales: this.Utils.sum(
          selectedLocationIds.map(
            (locationId) => locationSalesDict[locationId]?.projectedSales || 0
          )
        ),
        actualSales: this.Utils.sum(
          selectedLocationIds.map((locationId) => locationSalesDict[locationId]?.actualSales || 0)
        ),
      },
      byLocation,
    };
  }

  getSummary(data, selectedLocationIds) {
    const flattenedSummaryItems = this.getSchemaFields({
      laborUISchema: this.LaborUISchema.schema.laborReportUISchema,
    });
    const locationIds = selectedLocationIds.length
      ? selectedLocationIds
      : this.PersonaCollection.locations.map((location) => location.id);

    const summary = {};
    flattenedSummaryItems.forEach((item) => {
      summary[item.fieldName] = item.breakdownByLocation ? {} : 0;
    });

    Object.keys(data.cost).forEach((day) => {
      data.cost[day].forEach((reportEntity) => {
        this.tallyNonAggregated(summary, flattenedSummaryItems, reportEntity);
      });
    });

    const sales = this.getRelevantSales(data.revenue, locationIds);
    const aggregatedSummary = this.tallyAggregates(
      summary,
      locationIds,
      sales,
      flattenedSummaryItems
    );

    Object.assign(summary, aggregatedSummary);
    return summary;
  }

  tallyNonAggregated(summary, flattenedSummaryItems, reportEntity) {
    flattenedSummaryItems
      .filter((item) => !item.calculateOnAggregated)
      .forEach((item) => {
        if (item.breakdownByLocation) {
          if (!(reportEntity.location.id in summary[item.fieldName])) {
            summary[item.fieldName][reportEntity.location.id] = 0;
          }

          summary[item.fieldName][reportEntity.location.id] += this.Utils.evaluateExpression(
            item.formula,
            { data: reportEntity }
          );
        } else {
          summary[item.fieldName] += this.Utils.evaluateExpression(item.formula, {
            data: reportEntity,
          });
        }
      });
  }

  getFlattenedSummaryItems(items, relevantProps) {
    const flat = [];
    items.forEach((item) => {
      if (item.children) {
        flat.push(...this.getFlattenedSummaryItems(item.children.items, relevantProps));
      }

      const slimmedItem = this.Utils.extendWithProps(item, {}, relevantProps);
      flat.push(slimmedItem);
    });

    return flat;
  }

  migrateSchemaSalesReportColumns() {
    const table = SALES_UI_SCHEMA.table.items;
    const { clientFlags } = this.Authentication.user.settings;
    let oldModelActiveDict;

    if (!clientFlags.schemaSalesReportColumns) {
      clientFlags.schemaSalesReportColumns = [];
    }

    // insert new items
    const currentItemProperties = clientFlags.schemaSalesReportColumns.map((i) => i.property);
    const newItems = table.filter((item) => !currentItemProperties.includes(item.property));

    newItems.forEach((newItem) => {
      const index = table.indexOf(newItem);
      const predecessor = index > 0 ? table[index - 1] : null;
      const predecessorIndexInSettings = clientFlags.schemaSalesReportColumns.findIndex(
        (item) => item.property === predecessor?.property
      );
      clientFlags.schemaSalesReportColumns.splice(predecessorIndexInSettings + 1, 0, {
        property: newItem.property,
        active:
          oldModelActiveDict && newItem.property in oldModelActiveDict
            ? oldModelActiveDict[newItem.property]
            : newItem.activeByDefault,
      });
    });

    // remove osbolete items
    const tableProperties = table.map((item) => item.property);
    clientFlags.schemaSalesReportColumns = clientFlags.schemaSalesReportColumns.filter((item) =>
      tableProperties.includes(item.property)
    );
  }

  migrateSchemaLaborReportColumns() {
    let table = this.LaborUISchema.schema.laborReportUISchema.table.items;
    const { clientFlags } = this.Authentication.user.settings;
    let oldModelActiveDict;

    if (!clientFlags.schemaLaborReportColumns) {
      clientFlags.schemaLaborReportColumns = [];

      if (clientFlags.laborReportColumns) {
        oldModelActiveDict = clientFlags.laborReportColumns.reduce(
          (agg, item) => ((agg[item.property] = item.active), agg),
          {}
        );

        table = table.sort((a, b) => {
          const oldModelIndexA = clientFlags.laborReportColumns.findIndex(
            (i) => i.property === a.property
          );
          const oldModelIndexB = clientFlags.laborReportColumns.findIndex(
            (i) => i.property === b.property
          );

          if (oldModelIndexA >= 0 && oldModelIndexB >= 0) {
            return oldModelIndexA - oldModelIndexB;
          }

          return table.indexOf(a) - table.indexOf(b);
        });
      }
    }

    // insert new items
    const currentItemProperties = clientFlags.schemaLaborReportColumns.map((i) => i.property);
    const newItems = table.filter((item) => !currentItemProperties.includes(item.property));

    newItems.forEach((newItem) => {
      const index = table.indexOf(newItem);
      const predecessor = index > 0 ? table[index - 1] : null;
      const predecessorIndexInSettings = clientFlags.schemaLaborReportColumns.findIndex(
        (item) => item.property === predecessor?.property
      );
      clientFlags.schemaLaborReportColumns.splice(predecessorIndexInSettings + 1, 0, {
        property: newItem.property,
        active:
          oldModelActiveDict && newItem.property in oldModelActiveDict
            ? oldModelActiveDict[newItem.property]
            : newItem.activeByDefault,
      });
    });

    // remove osbolete items
    const tableProperties = table.map((item) => item.property);
    clientFlags.schemaLaborReportColumns = clientFlags.schemaLaborReportColumns.filter((item) =>
      tableProperties.includes(item.property)
    );
  }

  migrateSchemaPayrollReportColumns(oldFlag, newFlag) {
    let table = this.LaborUISchema.schema.payrollUISchema.table.items;
    const { clientFlags } = this.Authentication.user.settings;
    let oldModelActiveDict;

    if (!clientFlags[newFlag]) {
      clientFlags[newFlag] = [];

      if (clientFlags[oldFlag]) {
        oldModelActiveDict = clientFlags[oldFlag].reduce(
          (agg, item) => ((agg[item.property] = item.active), agg),
          {}
        );

        table = table.sort((a, b) => {
          const oldModelIndexA = clientFlags[oldFlag].findIndex((i) => i.property === a.property);
          const oldModelIndexB = clientFlags[oldFlag].findIndex((i) => i.property === b.property);

          if (oldModelIndexA >= 0 && oldModelIndexB >= 0) {
            return oldModelIndexA - oldModelIndexB;
          }

          return table.indexOf(a) - table.indexOf(b);
        });
      }
    }

    // insert new items
    const currentItemProperties = clientFlags[newFlag].map((i) => i.property);
    const newItems = table.filter((item) => !currentItemProperties.includes(item.property));

    newItems.forEach((newItem) => {
      const index = table.indexOf(newItem);
      const predecessor = index > 0 ? table[index - 1] : null;
      const predecessorIndexInSettings = clientFlags[newFlag].findIndex(
        (item) => item.property === predecessor?.property
      );
      clientFlags[newFlag].splice(predecessorIndexInSettings + 1, 0, {
        property: newItem.property,
        active:
          oldModelActiveDict && newItem.property in oldModelActiveDict
            ? oldModelActiveDict[newItem.property]
            : newItem.activeByDefault,
      });
    });

    // remove osbolete items
    const tableProperties = table.map((item) => item.property);
    clientFlags[newFlag] = clientFlags[newFlag].filter((item) =>
      tableProperties.includes(item.property)
    );
  }
}

export default ReportsUtils;
